import { useTranslation } from 'react-i18next';
import { Link, useLocation } from '@tanstack/react-router';
import { communityTrpc } from '@org/clients/community';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Skeleton,
} from '@org/ui';
import { Compass, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';

interface SpacesDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SpacesDrawer({ open, onOpenChange }: SpacesDrawerProps) {
  const { t } = useTranslation('community');
  const location = useLocation();
  const { data, isLoading } = communityTrpc.pinnedSpaces.list.useQuery();

  const pinnedSpaces = data?.pinnedSpaces ?? [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-[300px] p-0">
        <SheetHeader className="border-b border-border px-4 py-3">
          <SheetTitle>{t('spacesDrawer.title')}</SheetTitle>
        </SheetHeader>

        <nav className="flex-1 overflow-y-auto" aria-label={t('spacesDrawer.title')}>
          {isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full rounded-md" />
              ))}
            </div>
          ) : pinnedSpaces.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-sm text-muted-foreground">{t('spacesDrawer.noPinnedSpaces')}</p>
              <Link
                to="/spaces"
                className="mt-2 inline-block text-sm text-primary hover:underline"
                onClick={() => onOpenChange(false)}
              >
                {t('spacesDrawer.exploreSpaces')}
              </Link>
            </div>
          ) : (
            <ul className="p-2">
              {pinnedSpaces.map((space) => (
                <PinnedSpaceItem
                  key={space.spaceId}
                  space={space}
                  currentPath={location.pathname}
                  onNavigate={() => onOpenChange(false)}
                />
              ))}
            </ul>
          )}

          {pinnedSpaces.length > 0 && (
            <div className="border-t border-border p-3">
              <Link
                to="/spaces"
                className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                onClick={() => onOpenChange(false)}
              >
                <Compass className="h-4 w-4" />
                {t('spacesDrawer.findMoreSpaces')}
              </Link>
            </div>
          )}
        </nav>
      </SheetContent>
    </Sheet>
  );
}

interface PinnedSpaceItemProps {
  space: {
    spaceId: string;
    name: string;
    subPages: Array<{ subPageId: string; title: string }>;
  };
  currentPath: string;
  onNavigate: () => void;
}

function PinnedSpaceItem({ space, currentPath, onNavigate }: PinnedSpaceItemProps) {
  const { t } = useTranslation('community');
  const [expanded, setExpanded] = useState(false);
  const isActive = currentPath === `/spaces/${space.spaceId}`;
  const hasSubPages = space.subPages.length > 0;

  return (
    <li>
      <div className="flex items-center">
        {hasSubPages && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="mr-1 rounded p-1 text-muted-foreground hover:bg-muted"
            aria-label={expanded ? t('sidebar.collapse') : t('sidebar.expand')}
          >
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        )}
        <Link
          to="/spaces/$spaceId"
          params={{ spaceId: space.spaceId }}
          className={`flex-1 rounded-md px-3 py-2 text-sm transition-colors hover:bg-muted ${
            isActive ? 'bg-muted font-medium text-foreground' : 'text-foreground'
          }`}
          aria-current={isActive ? 'page' : undefined}
          onClick={onNavigate}
        >
          {space.name}
        </Link>
      </div>
      {hasSubPages && expanded && (
        <ul className="ml-7 space-y-0.5 pb-1">
          {space.subPages.map((subPage) => {
            const subPagePath = `/spaces/${space.spaceId}/pages/${subPage.subPageId}`;
            const isSubActive = currentPath === subPagePath;
            return (
              <li key={subPage.subPageId}>
                <Link
                  to="/spaces/$spaceId/pages/$pageId"
                  params={{ spaceId: space.spaceId, pageId: subPage.subPageId }}
                  className={`block rounded-md px-3 py-1.5 text-xs transition-colors hover:bg-muted ${
                    isSubActive ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground'
                  }`}
                  aria-current={isSubActive ? 'page' : undefined}
                  onClick={onNavigate}
                >
                  {subPage.title}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}
